import { useEffect, useState } from "react";
import { PenLine } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { usePoll } from "../../hooks/usePoll";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { StatusBadge } from "../StatusBadge";
import { NoManuscript } from "./NoManuscript";
import { useModels, ModelSelect } from "./shared";
import { ComplianceList } from "./StructureTab";
import { PremiumGate } from "../../pages/BillingPage";

const TEMPLATES = [
  ["Basic line", "“I’m not going back,” Mara said."],
  ["Action beat breaks speech", "“I’m not going back.” Mara set the lamp down. “Not tonight, not ever.”"],
  ["Question tag", "“Where is he?” Jory asked."],
  ["Tag mid-sentence", "“If the light fails,” she said, “the ship is lost.”"],
  ["New speaker, new paragraph", "“Ships are seen and not seen.”\n\n“That’s why we keep the light,” he answered."],
  ["Interrupted speech", "“I only meant—”\n\n“I know what you meant.”"],
];

function Templates() {
  return (
    <div className="panel p-5" data-testid="dialogue-templates">
      <p className="text-xs font-mono text-amber-400/80 mb-3">[Dialogue Mechanics] industry-standard formats</p>
      {TEMPLATES.map(([l, t]) => <div key={l} className="mb-3"><p className="text-xs text-slate-500">{l}</p><p className="manuscript text-sm text-slate-200 whitespace-pre-wrap indent-6">{t}</p></div>)}
      <p className="text-[11px] text-slate-500">Double quotation marks; comma inside closing quote before a tag; each speaker in a new indented paragraph; action beats instead of adverb-heavy tags.</p>
    </div>
  );
}

function Assistant({ book, version, reload }) {
  const models = useModels();
  const [f, setF] = useState({ chapter_id: version.chapters[version.chapters.length - 1].id, mode: "draft_next", prose_style: book.structure?.prose_style || "novel", instructions: "", target_words: 800, para_start: "", para_end: "", beat_key: "", model: "" });
  const [job, setJob] = useState(null);
  const [fw, setFw] = useState(null);
  useEffect(() => { api.get("/structure/frameworks").then((r) => setFw(r.data)); }, []);
  useEffect(() => { if (models && !f.model) setF((x) => ({ ...x, model: models.default })); }, [models]); // eslint-disable-line
  usePoll(() => api.get(`/write-jobs/${job.id}`).then((r) => setJob(r.data)), job?.status === "running");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const start = async () => {
    try { const { data } = await api.post(`/books/${book.id}/write`, { ...f, target_words: Number(f.target_words), para_start: f.para_start ? Number(f.para_start) : null, para_end: f.para_end ? Number(f.para_end) : null, beat_key: f.beat_key || null }); setJob({ id: data.id, status: "running" }); } catch (e) { toast.error(errMsg(e)); }
  };
  const insert = async () => { try { const { data } = await api.post(`/write-jobs/${job.id}/insert`); toast.success(`Inserted as v${data.number}`); setJob({ ...job, status: "inserted" }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const beats = fw?.frameworks[book.structure?.framework]?.beats || [];
  const sel = "h-9 rounded-md bg-slate-900 border border-slate-700 px-2 text-sm";
  return (
    <div className="space-y-4">
      <div className="panel p-5 grid md:grid-cols-2 gap-3" data-testid="write-setup">
        <select value={f.chapter_id} onChange={set("chapter_id")} className={sel} data-testid="write-chapter-select">{version.chapters.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</select>
        <select value={f.mode} onChange={set("mode")} className={sel} data-testid="write-mode-select"><option value="draft_next">Draft the next scene</option><option value="refine_section">Refine a section</option></select>
        <select value={f.prose_style} onChange={set("prose_style")} className={sel} data-testid="write-style-select">{fw && Object.entries(fw.styles).map(([k, s]) => <option key={k} value={k}>{s.name}</option>)}</select>
        <select value={f.beat_key} onChange={set("beat_key")} className={sel} data-testid="write-beat-select"><option value="">— Beat (from blueprint) —</option>{beats.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}</select>
        {f.mode === "draft_next" ? <Input type="number" value={f.target_words} onChange={set("target_words")} placeholder="Target words" data-testid="write-target-words" /> :
          <div className="flex gap-2"><Input type="number" placeholder="From ¶" value={f.para_start} onChange={set("para_start")} data-testid="write-para-start" /><Input type="number" placeholder="To ¶" value={f.para_end} onChange={set("para_end")} data-testid="write-para-end" /></div>}
        <ModelSelect value={f.model} onChange={(m) => setF({ ...f, model: m })} models={models} testId="write-model-select" />
        <Textarea placeholder="Instructions for this scene" value={f.instructions} onChange={set("instructions")} className="md:col-span-2 min-h-[60px]" data-testid="write-instructions" />
        <Button onClick={start} disabled={job?.status === "running"} className="md:col-span-2 bg-amber-600" data-testid="start-write-button"><PenLine size={14} className="mr-1" />{job?.status === "running" ? "Drafting…" : "Generate"}</Button>
      </div>
      {job && job.status !== "running" && (
        <div className="panel p-5 space-y-4" data-testid="write-result">
          <div className="flex justify-between items-center"><StatusBadge status={job.status === "ready" ? "review" : job.status} testId="write-status" />{job.result_words && <span className="text-xs font-mono text-slate-400">{job.result_words} words</span>}</div>
          {job.error && <p className="text-sm text-rose-300">{job.error}</p>}
          {job.text && <div className="manuscript text-sm text-slate-200 whitespace-pre-wrap max-h-[420px] overflow-y-auto scrollbar-thin" data-testid="write-text">{job.text}</div>}
          {job.compliance && <ComplianceList items={job.compliance} testId="write-compliance" />}
          {job.notes && Object.entries(job.notes).filter(([, v]) => v?.length).map(([g, v]) => <div key={g}><p className="text-xs font-mono text-amber-400/80">[{g}]</p>{v.map((n, i) => <p key={i} className="text-xs text-slate-400">• {n}</p>)}</div>)}
          {job.status === "ready" && <Button onClick={insert} className="bg-amber-600" data-testid="insert-write-button">Insert into chapter → new version</Button>}
        </div>
      )}
    </div>
  );
}

export default function WriteTab(props) {
  if (!props.version) return <NoManuscript />;
  return (
    <div className="grid xl:grid-cols-[1fr_340px] gap-6">
      <PremiumGate><Assistant {...props} /></PremiumGate>
      <Templates />
    </div>
  );
}
